import { Suspense, useLayoutEffect, useMemo, useRef } from 'react';
import { useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { PAINTS, SUITS, avatarFile, type ArmorChoice, type AvatarId } from '../game/armor';
import { Rival } from './Rival';

/** Standing height of every suit in world units (a room is 4 across); feet sit at FEET. */
const HEIGHT = 1.8;
const FEET = -1.15;

const extend = (loader: THREE.Loader) => (loader as GLTFLoader).setMeshoptDecoder(MeshoptDecoder);

/**
 * Recolours a suit in the shader: plates take the paint (keeping the texture's light and
 * wear), and the glowing strips, found by their warm saturated colour, take the side colour
 * and glow. One material per suit, paint and side, shared by every copy.
 */
function paintMaterial(src: THREE.MeshStandardMaterial, paint: number | null, side: number): THREE.MeshStandardMaterial {
  const m = src.clone();
  const uniforms = {
    uPaint: { value: new THREE.Color(paint ?? 0xffffff) },
    uPaintAmount: { value: paint === null ? 0 : 0.85 },
    uSide: { value: new THREE.Color(side) },
  };
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {', 'uniform vec3 uPaint;\nuniform float uPaintAmount;\nuniform vec3 uSide;\nvoid main() {\n  vec3 qoobdBase = vec3(0.0);')
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        {
          qoobdBase = diffuseColor.rgb;
          float lum = dot(qoobdBase, vec3(0.299, 0.587, 0.114));
          // Plates are dark; lift them so the paint reads, while keeping the texture's wear.
          diffuseColor.rgb = mix(qoobdBase, uPaint * clamp(0.5 + lum * 1.8, 0.0, 1.3), uPaintAmount);
        }`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        {
          float qoobdGlow;
          #ifdef USE_EMISSIVEMAP
            // The suit's own glow map marks its lamps and strips.
            qoobdGlow = smoothstep(0.08, 0.4, max(emissiveColor.r, max(emissiveColor.g, emissiveColor.b)));
          #else
            // No glow map: find the strips by their warm, saturated colour.
            float mx = max(qoobdBase.r, max(qoobdBase.g, qoobdBase.b));
            float mn = min(qoobdBase.r, min(qoobdBase.g, qoobdBase.b));
            float sat = (mx - mn) / max(mx, 1e-4);
            qoobdGlow = smoothstep(0.45, 0.75, sat) * smoothstep(0.12, 0.35, mx) * step(qoobdBase.b, qoobdBase.g) * step(qoobdBase.g, qoobdBase.r);
          #endif
          diffuseColor.rgb = mix(diffuseColor.rgb, uSide * 0.35, qoobdGlow);
          // Strips glow in the side colour only, never the suit's original warm light.
          totalEmissiveRadiance = uSide * qoobdGlow * 2.2;
        }`,
      );
  };
  m.customProgramCacheKey = () => `qoobd-armor-${paint ?? 'none'}-${side}`;
  return m;
}

const materialCache = new Map<string, THREE.MeshStandardMaterial>();

/** How the face behind the visor is shown. "hollow" is for mirror rooms only. */
export type FaceMode = 'normal' | 'hollow';

/** A soft oval, so the face fades into the dark of the helmet instead of ending in a square. */
let ovalMask: THREE.CanvasTexture | null = null;
function oval(): THREE.CanvasTexture {
  if (ovalMask) return ovalMask;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  g.fillStyle = '#000';
  g.fillRect(0, 0, 128, 128);
  const r = g.createRadialGradient(64, 60, 18, 64, 62, 62);
  r.addColorStop(0, '#fff');
  r.addColorStop(0.7, '#ddd');
  r.addColorStop(1, '#000');
  g.fillStyle = r;
  g.beginPath();
  g.ellipse(64, 62, 52, 62, 0, 0, Math.PI * 2);
  g.fill();
  ovalMask = new THREE.CanvasTexture(c);
  return ovalMask;
}

interface Visor {
  position: THREE.Vector3;
  size: number;
}

/** The face behind the visor glass: dimmer than the room, as if seen through tinted glass. */
function Face({ avatar, mode, visor, hollow }: { avatar: AvatarId; mode: FaceMode; visor: Visor; hollow: boolean }) {
  const files = hollow ? [avatarFile(avatar), avatarFile(avatar, true)] : [avatarFile(avatar)];
  const textures = useLoader(THREE.TextureLoader, files);
  const material = useMemo(() => {
    for (const t of textures) t.colorSpace = THREE.SRGBColorSpace;
    // Lit from inside the helmet: a little brighter than the texture, so the face reads through the glass.
    return new THREE.MeshBasicMaterial({ alphaMap: oval(), transparent: true, depthWrite: false, color: new THREE.Color(1.45, 1.4, 1.32), fog: true });
  }, [textures]);
  const map = mode === 'hollow' && textures[1] ? textures[1] : textures[0]!;
  if (material.map !== map) {
    material.map = map;
    material.needsUpdate = true;
  }
  return (
    <mesh position={visor.position} material={material} renderOrder={2}>
      <planeGeometry args={[visor.size, visor.size * 1.12]} />
    </mesh>
  );
}

/** Puts every mesh under `obj` on one render layer (layers don't inherit in three.js). */
export function setLayer(obj: THREE.Object3D, layer: number): void {
  obj.traverse((o) => o.layers.set(layer));
}

interface SuitProps {
  armor: ArmorChoice;
  side: number;
  face?: FaceMode;
  /** Load the hollow face too (the player's own reflection). */
  hollow?: boolean;
}

function Suit({ armor, side, face = 'normal', hollow = false }: SuitProps) {
  const gltf = useLoader(GLTFLoader, SUITS[armor.suit].file, extend);
  const built = useMemo(() => {
    const root = gltf.scene.clone(true);
    const paint = PAINTS[armor.paint].hex;
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const src = mesh.material as THREE.MeshStandardMaterial;
      const key = `${armor.suit}|${src.uuid}|${armor.paint}|${side}`;
      let m = materialCache.get(key);
      if (!m) {
        m = paintMaterial(src, paint, side);
        materialCache.set(key, m);
      }
      mesh.material = m;
    });
    // Normalise: a fixed height, feet on the floor line, centred, facing -Z like the camera.
    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(new THREE.Vector3());
    const scale = HEIGHT / Math.max(size.y, 1e-3);
    root.scale.setScalar(scale);
    root.position.set(-((box.min.x + box.max.x) / 2) * scale, FEET - box.min.y * scale, -((box.min.z + box.max.z) / 2) * scale);
    root.updateMatrixWorld(true);
    const body = new THREE.Group();
    body.add(root);
    return { body, visor: findVisor(armor.suit) };
  }, [gltf, armor.suit, armor.paint, side]);
  return (
    <group rotation={[0, Math.PI, 0]}>
      {/* models face +Z; agents face -Z */}
      <primitive object={built.body} />
      <Face avatar={armor.avatar} mode={face} visor={built.visor} hollow={hollow} />
    </group>
  );
}

/** Where the face sits: each suit's visor centre and size, measured on the normalised model. */
function findVisor(suit: ArmorChoice['suit']): Visor {
  let fit = SUITS[suit].visor;
  // Development only: ?visor=y,z,size overrides the fit for lining faces up.
  if (import.meta.env.DEV) {
    const q = new URLSearchParams(location.search).get('visor');
    const n = q?.split(',').map(Number);
    if (n && n.length === 3 && n.every(Number.isFinite)) fit = { y: n[0]!, z: n[1]!, size: n[2]! };
  }
  return { position: new THREE.Vector3(0, fit.y, fit.z), size: fit.size };
}

/** A player's armor. Shows the simple placeholder while the model loads (or if it can't). */
export function Armor({ armor, side, face, hollow, layer }: SuitProps & { layer?: number }) {
  const g = useRef<THREE.Group>(null);
  // Layers don't inherit, so re-apply after the model (and its face) mount.
  useLayoutEffect(() => {
    if (layer === undefined || !g.current) return;
    const el = g.current;
    setLayer(el, layer);
    const id = setInterval(() => setLayer(el, layer), 500);
    return () => clearInterval(id);
  });
  return (
    <group ref={g}>
      <Suspense fallback={<Rival color={side} />}>
        <Suit armor={armor} side={side} face={face} hollow={hollow} />
      </Suspense>
    </group>
  );
}

/** Start fetching every suit early so they're ready when a rival appears. */
export function preloadArmor(): void {
  for (const s of Object.values(SUITS)) useLoader.preload(GLTFLoader, s.file, extend);
}
